import useAppStore from "@/context/store";
import { getGrocery } from "@/services";
import { getExpsValue } from "@/tracker/abTest";
import { useTodayLocalState } from "@/utils/hooks/useCloseToday";
import useReq from "@/utils/useReq";
import { useMemoizedFn } from "ahooks";
import { isNumber } from "lodash";
import { useEffect, useMemo, useRef } from "react";
import LoginDialog from ".";

interface Config {
  page: PageConf;
  search: SearchConf;
}

interface PageConf {
  page: number;
  allEnable: boolean;
  all: number;
  closable: boolean;
  abTest: PageConf[];
  ab?: string;
}

interface SearchConf {
  ab?: string;
  enable: boolean;
  limit: number;
  closeable: boolean;
  disableNext?: boolean;
  abTest: SearchConf[];
}

function useConfig<T extends keyof Config>(type: T, expireVarName: string) {
  const { data } = useReq(
    async () => {
      return Promise.all([
        getGrocery<Config>("view_limit"),
        getExpsValue(expireVarName, "v0"),
      ]);
    },
    {
      cacheKey: "limit_config_cache",
      staleTime: -1,
    }
  );

  return useMemo(() => {
    if (!data) return;
    const [config, ab] = data;
    // @ts-ignore
    return (config?.[type]?.abTest?.find((item) => item.ab === ab) ||
      config?.[type]) as Config[T];
  }, [data, type]);
}

interface ILoadingLimit {
  pageListData?: any[];
  ignore?: boolean;
  pageSize?: number;
}

// 登录加载访问限制
export function useLoadingLimit({
  pageListData = [],
  ignore = false,
  pageSize = 0,
}: ILoadingLimit = {}) {
  const { user, userReady } = useAppStore((s) => ({
    user: s.user,
    userReady: s.userReady,
  }));

  const limitConfig = useConfig("page", "login_limit");

  // 开关， 未登录，忽略场景，配置有设置限制
  const enable = useMemo(() => {
    return !user && userReady && !ignore && limitConfig;
  }, [user, userReady, ignore, limitConfig]);

  const [visitedNum, setVisitedNum] = useTodayLocalState<number>(
    "page_view_limit",
    {
      defaultValue: { value: 0 },
    }
  );

  const pageSizeRef = useRef<number>(pageSize);
  // 是否触发当前页面访问限制
  const pageVisitLimit = useMemo(() => {
    if (!enable) return false;

    if (!pageSizeRef.current && pageListData?.length) {
      // 记录每页的数量
      pageSizeRef.current = pageListData.length;
    }

    if (limitConfig && pageSizeRef.current) {
      return (
        pageListData?.length >= (limitConfig?.page || 2) * pageSizeRef.current
      );
    }

    return false;
  }, [enable, pageListData?.length, limitConfig]);

  useEffect(() => {
    if (!enable) return;

    // 记录第二屏的加载记录
    if (pageListData?.length > pageSizeRef.current) {
      setVisitedNum((prev) => {
        return (prev || 0) + 1;
      });
    }
  }, [enable, pageListData, setVisitedNum]);

  // 是否触发总次数限制
  const allLimit = useMemo(() => {
    if (!enable || !limitConfig?.allEnable) return false;

    if (isNumber(visitedNum) && limitConfig && isNumber(limitConfig?.all)) {
      return visitedNum >= limitConfig.all;
    }
  }, [enable, visitedNum, limitConfig]);

  const onLoadLimitCheck = useMemoizedFn(() => {
    return new Promise((resolve) => {
      const closable = limitConfig?.closable || !allLimit;
      if (!user && userReady && (pageVisitLimit || allLimit)) {
        LoginDialog.open({
          onOk: () => resolve(true),
          closable: closable,
          keyboard: closable,
          maskClosable: closable,
        });

        return;
      }

      setVisitedNum((prev) => {
        return (prev || 0) + 1;
      });

      resolve(true);
    });
  });

  return {
    limit: pageVisitLimit || allLimit,
    pageVisitLimit,
    allLimit,
    onLoadLimitCheck,
    config: limitConfig,
  };
}

export function useSearchLimit() {
  const user = useAppStore((s) => s.user);
  const limitConfig = useConfig("search", "search_limit");

  const [searchTime, setSearchTimes] = useTodayLocalState<number>(
    "search_limit",
    {
      defaultValue: { value: 0 },
    }
  );

  const onSearchLimitCheck = useMemoizedFn(() => {
    return new Promise((resolve, reject) => {
      if (user || !limitConfig?.enable) {
        resolve(true);
        return;
      }

      if ((searchTime as number) >= limitConfig?.limit) {
        const closable = limitConfig?.closeable;
        // 触发限制，弹登录弹窗
        LoginDialog.open({
          onOk: () => limitConfig?.disableNext ?? resolve(true),
          mask: closable,
          maskClosable: closable,
          keyboard: closable,
        });

        if (!limitConfig?.disableNext) {
          resolve(true);
        }
      } else {
        setSearchTimes((searchTime || 0) + 1);
        resolve(true);
      }
    });
  });

  return {
    onSearchLimitCheck,
  };
}
